/**
 * Sister Diamonds Platform
 * 
 * Copyright (c) 2024-2026 Sister Diamonds, LLC. All rights reserved.
 * A Volition Enterprises Company.
 * 
 * This software is proprietary and confidential. Unauthorized copying,
 * modification, distribution, or use of this software, via any medium,
 * is strictly prohibited without express written permission.
 * 
 * Contact: info@sisterdiamonds.com
 */

import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CelebrationProvider } from "@/context/CelebrationContext";
import axios from "axios";

// Components
import SHEChatWidget from "@/components/SHEChatWidget";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";
import INeedSupportButton from "@/components/INeedSupportButton";
import ScrollToTop from "@/components/ScrollToTop";

// Pages
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import DashboardPage from "@/pages/DashboardPage";
import OnboardingPage from "@/pages/OnboardingPage";
import PathwaysPage from "@/pages/PathwaysPage";
import InnerCirclePage from "@/pages/InnerCirclePage";
import CoachesPage from "@/pages/CoachesPage";
import CoachProfilePage from "@/pages/CoachProfilePage";
import CommunitiesPage from "@/pages/CommunitiesPage";
import CommunityDetailPage from "@/pages/CommunityDetailPage";
import VideoLibraryPage from "@/pages/VideoLibraryPage";
import VideoPlayerPage from "@/pages/VideoPlayerPage";
import MessagesPage from "@/pages/MessagesPage";
import CalendarPage from "@/pages/CalendarPage";
import SessionsPage from "@/pages/SessionsPage";
import DiagnosticPage from "@/pages/DiagnosticPage";
import InnerEdgeHubPage from "@/pages/InnerEdgeHubPage";
import InnerEdgePaymentPage from "@/pages/InnerEdgePaymentPage";
import RetreatHubPage from "@/pages/RetreatHubPage";
import RetreatLandingPage from "@/pages/RetreatLandingPage";
import RetreatBookingPage from "@/pages/RetreatBookingPage";
import SetupAccountPage from "@/pages/SetupAccountPage";
import SDCLoungePage from "@/pages/SDCLoungePage";
import SDCHubPage from "@/pages/SDCHubPage";
import SettingsPage from "@/pages/SettingsPage";
import AdminPage from "@/pages/AdminPage";
import WonderTwinDashboardPage from "@/pages/WonderTwinDashboardPage";
import CheckoutSuccessPage from "@/pages/CheckoutSuccessPage";
import CoachDashboardPage from "@/pages/CoachDashboardPage";
import MemberDirectoryPage from "@/pages/MemberDirectoryPage";
import JournalPage from "@/pages/JournalPage";
import AdminReportsPage from "@/pages/AdminReportsPage";
import SavedPostsPage from "@/pages/SavedPostsPage";
import CirclesPage from "@/pages/CirclesPage";
import CircleDetailPage from "@/pages/CircleDetailPage";
import CommunityGuidelinesPage from "@/pages/CommunityGuidelinesPage";
import BooksPage from "@/pages/BooksPage";
import FounderProfilePage from "@/pages/FounderProfilePage";
import GiftMembershipPage from "@/pages/GiftMembershipPage";
import AcceptGiftPage from "@/pages/AcceptGiftPage";
import MilestoneNotification from "@/components/MilestoneNotification";

// Protected Route Component (requires login)
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cream">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-forest"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Tier-gated Route Component
const TierRoute = ({ children, requiredTiers, redirectTo = "/pathways" }) => {
  const { user, loading, hasAnyTier } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cream">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-forest"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!hasAnyTier(requiredTiers)) {
    return <Navigate to={redirectTo} replace />;
  }
  
  return children;
};

// Admin Route Component
const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cream">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-forest"></div>
      </div>
    );
  }
  
  if (!user || user.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Coach Route Component (for coaches and admins)
const CoachRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cream">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-forest"></div>
      </div>
    );
  }
  
  // Allow coaches and admins
  if (!user || (!user.is_coach && user.role !== 'admin')) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function App() {
  return (
    <AuthProvider>
      <CelebrationProvider>
        <div className="App min-h-screen bg-cream">
          <BrowserRouter>
            <ScrollToTop />
            <Routes>
            {/* Public Routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/setup-account" element={<SetupAccountPage />} />
            <Route path="/pathways" element={<PathwaysPage />} />
            <Route path="/inner-circle" element={<InnerCirclePage />} />
            <Route path="/the-inner-edge" element={<InnerEdgePaymentPage />} />
            <Route path="/coaches" element={<CoachesPage />} />
            <Route path="/coaches/founder-crystal-nicole" element={<FounderProfilePage />} />
            <Route path="/coaches/:coachId" element={<CoachProfilePage />} />
            
            {/* Public Retreat Page (accessible without login) */}
            <Route path="/costa-rica-retreat" element={<RetreatLandingPage />} />
            <Route path="/just-be-retreat" element={<RetreatLandingPage />} />
            <Route path="/retreat-booking" element={<RetreatBookingPage />} />
            
            {/* Gift Membership (public page to accept gifts) */}
            <Route path="/accept-gift/:giftCode" element={<AcceptGiftPage />} />
            
            {/* Protected Routes (login required) */}
            <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
            <Route path="/onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
            <Route path="/checkout/success" element={<ProtectedRoute><CheckoutSuccessPage /></ProtectedRoute>} />
            <Route path="/gift-membership" element={<ProtectedRoute><GiftMembershipPage /></ProtectedRoute>} />
            
            {/* Inner Circle+ Routes */}
            <Route path="/communities" element={
              <TierRoute requiredTiers={['inner_circle', 'inner_edge', 'coaching_activation', 'sdc']}>
                <CommunitiesPage />
              </TierRoute>
            } />
            <Route path="/communities/:communityId" element={
              <TierRoute requiredTiers={['inner_circle', 'inner_edge', 'coaching_activation', 'sdc']}>
                <CommunityDetailPage />
              </TierRoute>
            } />
            <Route path="/videos" element={
              <ProtectedRoute>
                <VideoLibraryPage />
              </ProtectedRoute>
            } />
            <Route path="/videos/:videoId" element={
              <ProtectedRoute>
                <VideoPlayerPage />
              </ProtectedRoute>
            } />
            <Route path="/messages" element={
              <TierRoute requiredTiers={['inner_circle', 'inner_edge', 'coaching_activation', 'sdc']}>
                <MessagesPage />
              </TierRoute>
            } />
            <Route path="/messages/:partnerId" element={
              <TierRoute requiredTiers={['inner_circle', 'inner_edge', 'coaching_activation', 'sdc']}>
                <MessagesPage />
              </TierRoute>
            } />
            
            {/* Inner Edge+ Routes */}
            <Route path="/diagnostic" element={
              <TierRoute requiredTiers={['inner_edge']}>
                <DiagnosticPage />
              </TierRoute>
            } />
            <Route path="/inner-edge" element={
              <TierRoute requiredTiers={['inner_edge']}>
                <InnerEdgeHubPage />
              </TierRoute>
            } />
            
            {/* Coaching Routes */}
            <Route path="/sessions" element={
              <TierRoute requiredTiers={['inner_edge', 'coaching_activation', 'sdc']}>
                <SessionsPage />
              </TierRoute>
            } />
            <Route path="/calendar" element={
              <TierRoute requiredTiers={['inner_edge', 'coaching_activation', 'sdc']}>
                <CalendarPage />
              </TierRoute>
            } />
            
            {/* Costa Rica Routes */}
            <Route path="/retreat" element={
              <TierRoute requiredTiers={['costa_rica']}>
                <RetreatHubPage />
              </TierRoute>
            } />
            
            {/* SDC Routes */}
            <Route path="/sdc" element={
              <TierRoute requiredTiers={['sdc']}>
                <SDCHubPage />
              </TierRoute>
            } />
            <Route path="/sdc-lounge" element={
              <TierRoute requiredTiers={['sdc']}>
                <SDCLoungePage />
              </TierRoute>
            } />
            
            {/* Member Directory & Journal Routes */}
            <Route path="/directory" element={
              <TierRoute requiredTiers={['inner_circle', 'inner_edge', 'coaching_activation', 'sdc']}>
                <MemberDirectoryPage />
              </TierRoute>
            } />
            <Route path="/journal" element={
              <TierRoute requiredTiers={['inner_circle', 'inner_edge', 'coaching_activation', 'sdc']}>
                <JournalPage />
              </TierRoute>
            } />
            <Route path="/saved" element={
              <TierRoute requiredTiers={['inner_circle', 'inner_edge', 'coaching_activation', 'sdc']}>
                <SavedPostsPage />
              </TierRoute>
            } />
            <Route path="/books" element={
              <TierRoute requiredTiers={['inner_circle', 'inner_edge', 'coaching_activation', 'sdc']}>
                <BooksPage />
              </TierRoute>
            } />
            
            {/* Community Circles Routes */}
            <Route path="/circles" element={
              <TierRoute requiredTiers={['inner_circle', 'inner_edge', 'coaching_activation', 'sdc']}>
                <CirclesPage />
              </TierRoute>
            } />
            <Route path="/circles/:circleId" element={
              <TierRoute requiredTiers={['inner_circle', 'inner_edge', 'coaching_activation', 'sdc']}>
                <CircleDetailPage />
              </TierRoute>
            } />
            
            {/* Community Guidelines */}
            <Route path="/community-guidelines" element={
              <ProtectedRoute>
                <CommunityGuidelinesPage />
              </ProtectedRoute>
            } />
            
            {/* Coach Dashboard Routes */}
            <Route path="/coach" element={<CoachRoute><CoachDashboardPage /></CoachRoute>} />
            <Route path="/coach/clients" element={<CoachRoute><CoachDashboardPage /></CoachRoute>} />
            <Route path="/coach/clients/:userId" element={<CoachRoute><CoachDashboardPage /></CoachRoute>} />
            <Route path="/coach/notes" element={<CoachRoute><CoachDashboardPage /></CoachRoute>} />
            <Route path="/coach/invoices" element={<CoachRoute><CoachDashboardPage /></CoachRoute>} />
            <Route path="/coach/agreements" element={<CoachRoute><CoachDashboardPage /></CoachRoute>} />
            <Route path="/coach/notifications" element={<CoachRoute><CoachDashboardPage /></CoachRoute>} />
            <Route path="/coach/messages" element={<CoachRoute><CoachDashboardPage /></CoachRoute>} />
            <Route path="/coach/messages/:partnerId" element={<CoachRoute><CoachDashboardPage /></CoachRoute>} />
            
            {/* Admin Routes */}
            <Route path="/admin" element={<AdminRoute><AdminPage /></AdminRoute>} />
            <Route path="/admin/users" element={<AdminRoute><AdminPage /></AdminRoute>} />
            <Route path="/admin/coaches" element={<AdminRoute><AdminPage /></AdminRoute>} />
            <Route path="/admin/group-sessions" element={<AdminRoute><AdminPage /></AdminRoute>} />
            <Route path="/admin/requests" element={<AdminRoute><AdminPage /></AdminRoute>} />
            <Route path="/admin/coach-notes" element={<AdminRoute><AdminPage /></AdminRoute>} />
            <Route path="/admin/invoices" element={<AdminRoute><AdminPage /></AdminRoute>} />
            <Route path="/admin/messages" element={<AdminRoute><AdminPage /></AdminRoute>} />
            <Route path="/admin/support" element={<AdminRoute><AdminPage /></AdminRoute>} />
            <Route path="/admin/safety" element={<AdminRoute><AdminPage /></AdminRoute>} />
            <Route path="/admin/esign" element={<AdminRoute><AdminPage /></AdminRoute>} />
            <Route path="/admin/reports" element={<AdminRoute><AdminReportsPage /></AdminRoute>} />
            <Route path="/admin/*" element={<AdminRoute><AdminPage /></AdminRoute>} />
            
            {/* Wonder Twin Dashboard */}
            <Route path="/wonder-twin" element={<AdminRoute><WonderTwinDashboardPage /></AdminRoute>} />
          </Routes>
          <SHEChatWrapper />
          <MilestoneNotificationWrapper />
        </BrowserRouter>
        <Toaster position="top-right" richColors />
        <PWAInstallPrompt />
      </div>
      </CelebrationProvider>
    </AuthProvider>
  );
}

// Wrapper for Milestone Notification - only shows on dashboard for logged-in users
const MilestoneNotificationWrapper = () => {
  const { user } = useAuth();
  const location = useLocation();
  
  // Only show on dashboard
  if (!user || location.pathname !== '/dashboard') {
    return null;
  }
  
  return <MilestoneNotification />;
};

// Wrapper component to provide page context to SHE chat
const SHEChatWrapper = () => {
  const location = useLocation();
  
  const getPageContext = () => {
    const path = location.pathname;
    
    const pageContextMap = {
      '/': `
        Page: Sister Diamonds Landing Page
        This is the main landing page for Sister Diamonds Collective.
        Key offerings:
        - Inner Circle membership ($97/month): Access to community, video library, weekly transmissions
        - Just Be: Soft Power Retreat in Costa Rica (June 26 - July 1, 2026): A luxury retreat for high-capacity women
        - Coaching programs including Inner Edge ($5,000) and SDC ($25,000)
        The mission is to help high-achieving women leaders find balance, restore their energy, and lead with both strength and softness.
      `,
      '/costa-rica-retreat': `
        Page: Just Be: Soft Power™ Retreat - Costa Rica
        Location: Imiloa Institute, Costa Rica
        Dates: June 26 – July 1, 2026
        This is a soul-deep, luxury retreat for high-capacity women ready to release, restore, and rise in their softness, not their striving.
        Hosts: Crystal and Dr. Nicole (the "Wonder Twins")
        Features: Luxury accommodations, transformational experiences, sisterhood, rest and restoration
        Pricing: Various accommodation options available
        To learn more or express interest, visitors can book a connection call.
      `,
      '/just-be-retreat': `
        Page: Just Be: Soft Power™ Retreat - Costa Rica
        Location: Imiloa Institute, Costa Rica
        Dates: June 26 – July 1, 2026
        This is a soul-deep, luxury retreat for high-capacity women ready to release, restore, and rise in their softness, not their striving.
      `,
      '/login': `
        Page: Login
        Users can log in to access their Sister Diamonds membership, coaching sessions, and community features.
      `,
      '/register': `
        Page: Register / Join Inner Circle
        New members can sign up for the Inner Circle membership ($97/month).
        Benefits include: Community access, video library, weekly transmissions, and connection with like-minded women leaders.
      `,
      '/dashboard': `
        Page: Member Dashboard
        This is the main dashboard for logged-in members showing their membership status, upcoming sessions, and quick access to features.
      `,
      '/coaches': `
        Page: Coaches Directory
        Browse the full directory of certified Sister Diamonds coaches.
        Each coach has specialties and can help with different aspects of leadership, personal development, and transformation.
      `,
      '/communities': `
        Page: Communities
        Access to various community spaces for discussion, support, and connection with other members.
      `,
      '/videos': `
        Page: Video Library
        Access to training videos, workshops, and educational content for members.
      `,
      '/diagnostic': `
        Page: Diagnostic Assessment
        A comprehensive LLM-powered diagnostic to assess your current state across multiple life dimensions.
        Results help match you with the right coach and provide insights for your growth journey.
      `,
      '/inner-edge': `
        Page: Inner Edge Hub
        The Inner Edge program ($5,000) includes:
        - Comprehensive diagnostic assessment
        - Coach matching based on your results
        - 6 coaching sessions with your matched coach
        - 2 founder sessions with Crystal and Dr. Nicole
        - Full community and app access
      `,
      '/sdc': `
        Page: SDC (Sister Diamond Collective) Hub
        The SDC program ($25,000 VIP tier) includes:
        - 3 chemistry sessions with different coaches (15 minutes each) to find your perfect match
        - Selection of your dedicated coach
        - 12 one-hour coaching sessions with your selected coach
        - 3 Wonder Twin sessions with Crystal and Dr. Nicole
        - 10% discount on all retreats and masterminds
        - Full app and community access
        - SDC Lounge access with Hot Seat and Concierge services
      `,
      '/sdc-lounge': `
        Page: SDC Lounge
        VIP-only space for SDC members featuring:
        - Hot Seat sessions: Submit topics for live coaching
        - Concierge service: Personal assistance and introductions
        - Exclusive VIP events and content
      `,
      '/pathways': `
        Page: Pathways
        Explore different membership tiers and coaching programs:
        - Inner Circle ($97/month): Community and content access
        - Inner Edge ($5,000): Comprehensive coaching program
        - SDC ($25,000): VIP transformation experience
      `,
      '/sessions': `
        Page: Sessions
        View and manage your upcoming and past coaching sessions.
        Book new sessions with your coach or the founders.
      `,
      '/settings': `
        Page: Settings
        Manage your account settings, profile information, and preferences.
      `
    };
    
    return pageContextMap[path] || `Page: ${path}\nThis is a page within the Sister Diamonds platform.`;
  };

  return <SHEChatWidget pageContext={getPageContext()} />;
}

export default App;
